import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../shared/services/apiClient';
import './register.css';

const registerSchema = z.object({
    firstName: z.string().min(2, 'Min 2 characters').regex(/^[a-zA-Z]+$/, 'Letters only'),
    lastName:  z.string().min(2, 'Min 2 characters').regex(/^[a-zA-Z]+$/, 'Letters only'),
    email:     z.string().email('Enter a valid email'),
    phone:     z.string().min(10, 'Min 10 digits').max(15, 'Max 15 digits').regex(/^\d+$/, 'Digits only'),
    password:  z.string()
        .min(8, 'Minimum 8 characters')
        .regex(/[A-Z]/, 'Must contain uppercase letter')
        .regex(/[0-9]/, 'Must contain a number')
        .regex(/[@#$%^&*]/, 'Must contain a special character (@#$%^&*)'),
});

type RegisterForm = z.infer<typeof registerSchema>;

export const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState(false);

    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<RegisterForm>({
        resolver: zodResolver(registerSchema),
    });

    const onSubmit = async (data: RegisterForm) => {
        setServerError('');
        try {
            await apiClient.post('/auth/register', data);
            setSuccess(true);
        } catch (err: any) {
            setServerError(err.response?.data?.message ?? 'Registration failed. Please try again.');
        }
    };

    if (success) {
        return (
            <div className="register-wrapper">
                <div className="register-card">
                    <div className="register-success">
                        <h2>Registration Successful!</h2>
                        <p>We've sent a verification link to your email. Please verify to activate your account.</p>
                        <button onClick={() => navigate('/login')} className="register-btn">Go to Login</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="register-wrapper">
                <div className="register-card">
                    <div className="register-header">
                        <h1>Create Account</h1>
                        <p>Join Arte Craft India today</p>
                    </div>

                    {serverError && <div className="register-error">{serverError}</div>}

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input {...register('firstName')} placeholder="First name" />
                                {errors.firstName && <span className="field-error">{errors.firstName.message}</span>}
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input {...register('lastName')} placeholder="Last name" />
                                {errors.lastName && <span className="field-error">{errors.lastName.message}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Email Address</label>
                            <input type="email" {...register('email')} placeholder="you@example.com" />
                            {errors.email && <span className="field-error">{errors.email.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Mobile Number</label>
                            <input type="tel" {...register('phone')} placeholder="10-15 digit number" />
                            {errors.phone && <span className="field-error">{errors.phone.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Password</label>
                            <input type="password" {...register('password')} placeholder="Min 8 chars, uppercase, number, symbol" />
                            {errors.password && <span className="field-error">{errors.password.message}</span>}
                        </div>

                        <button type="submit" className="register-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating account…' : 'Create Account'}
                        </button>
                    </form>

                    <div className="register-footer">
                        <p>Already have an account? <Link to="/login">Sign in</Link></p>
                    </div>
                </div>
            </div>
        </>
    );
};
